import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Store,
  Send,
  CheckCircle2,
  Phone,
  User,
  Mail,
  MapPin,
  Briefcase,
  Sparkles,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { useI18n } from "@project/lib/use-i18n";
import { franchiseEnquiryService } from "@project/services/user";

export interface GruhUdhyogFranchiseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GruhUdhyogFranchiseModal({
  open,
  onOpenChange,
}: GruhUdhyogFranchiseModalProps) {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    experience: "",
    message: "",
  });

  const resetForm = () => {
    setForm({
      name: "",
      phone: "",
      email: "",
      city: "",
      experience: "",
      message: "",
    });
    setSubmitted(false);
  };

  const handleClose = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        resetForm();
      }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      projectToast.error(t("account.visitorInquiryForm.validation.nameRequired") || "Please enter your name");
      return;
    }
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10) {
      projectToast.error("Please enter a valid 10-digit phone number");
      return;
    }
    if (!form.city.trim()) {
      projectToast.error("Please enter your city / location");
      return;
    }

    setSubmitting(true);
    try {
      await franchiseEnquiryService.create({
        full_name: form.name.trim(),
        phone_number: form.phone.trim(),
        email_address: form.email.trim() || undefined,
        city_location: form.city.trim(),
        business_experience: form.experience.trim() || undefined,
        message: form.message.trim() || undefined,
      });
      setSubmitted(true);
      projectToast.success(t("programs.gruhUdhyog.franchise.successMessage"));
    } catch {
      projectToast.error("Could not submit enquiry. Please try again or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl rounded-3xl p-6 sm:p-8 bg-card border-border">
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h3 className="text-2xl font-bold font-serif text-foreground">
              {t("programs.gruhUdhyog.franchise.successTitle")}
            </h3>
            <p className="mx-auto max-w-md text-sm text-muted-foreground leading-relaxed">
              {t("programs.gruhUdhyog.franchise.successMessage")}
            </p>

            <div className="mt-6 rounded-2xl border border-border bg-secondary/30 p-4 text-left">
              <p className="text-xs font-semibold text-foreground mb-3">
                {t("programs.gruhUdhyog.franchise.callDirect")}
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="tel:+919662544565"
                  className="flex items-center gap-2 rounded-xl bg-background border border-border px-3 py-2 text-xs font-bold text-foreground hover:border-primary transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 text-primary" /> +91 96625 44565
                </a>
                <a
                  href="tel:+919978930960"
                  className="flex items-center gap-2 rounded-xl bg-background border border-border px-3 py-2 text-xs font-bold text-foreground hover:border-primary transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 text-primary" /> +91 99789 30960
                </a>
              </div>
            </div>

            <Button
              className="mt-4 rounded-full bg-primary text-primary-foreground px-8"
              onClick={() => handleClose(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-2 text-left">
              <div className="inline-flex items-center gap-2 self-start rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                <Store className="h-3.5 w-3.5" />
                <span>{t("programs.gruhUdhyog.franchise.badge")}</span>
              </div>
              <DialogTitle className="text-2xl font-bold font-serif text-foreground">
                {t("programs.gruhUdhyog.franchise.modalTitle")}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {t("programs.gruhUdhyog.franchise.modalSubtitle")}
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="franchise-name" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.franchise.fullName")}
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="franchise-name"
                      placeholder="e.g. Ramesh Patel"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-9 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="franchise-phone" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.franchise.phoneNumber")}
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="franchise-phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-9 rounded-xl"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="franchise-email" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.franchise.email")}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="franchise-email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-9 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="franchise-city" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.franchise.cityLocation")}
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="franchise-city"
                      placeholder="e.g. Ahmedabad, Surat, Mumbai"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="pl-9 rounded-xl"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="franchise-exp" className="text-xs font-semibold">
                  {t("programs.gruhUdhyog.franchise.experience")}
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="franchise-exp"
                    placeholder="e.g. Retail shop owner / FMCG distributor / New entrepreneur"
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    className="pl-9 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="franchise-message" className="text-xs font-semibold">
                  {t("programs.gruhUdhyog.franchise.message")}
                </Label>
                <Textarea
                  id="franchise-message"
                  rows={3}
                  placeholder="Tell us about your interest, target area, or questions..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-xl resize-none"
                />
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  No royalty fees • 100% seva-aligned
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto rounded-full bg-emerald-800 hover:bg-emerald-900 text-white px-7 font-bold text-sm shadow-md"
                >
                  {submitting ? (
                    t("programs.gruhUdhyog.franchise.submitting")
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {t("programs.gruhUdhyog.franchise.submit")}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
